import { ReactNode, useMemo, useState } from 'react';
import { ReportContext } from 'assets/js/pages/reports/ReportContext';
import { ReportTableProvider } from 'assets/js/pages/reports/ReportTableProvider';
import { DateRange } from 'assets/js/pages/reports/types';
import { getInitialState } from 'assets/js/pages/reports/util';
import { RelativeDateRangeOperator } from 'components/filter/types';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { useTableSchema } from 'customHooks/useTableSchema';
import { ColumnSchema, ColumnSortDirection, GraphType } from 'types/domain';

interface ReportDetailProviderProps {
    children: ReactNode;
}

const ReportDetailProvider = (props: ReportDetailProviderProps) => {
    const { children } = props;
    const { data: report } = useCurrentReport();
    const { data } = useTableSchema(report.resource);
    const initialReportState = getInitialState(report, data);
    const [dateRange, setDateRange] = useState<DateRange>(initialReportState.dateRange);
    const [dateRangeType, setDateRangeType] = useState<RelativeDateRangeOperator>(
        initialReportState.dateRangeType,
    );
    const [chartColumn, setChartColumn] = useState<ColumnSchema | undefined>(
        initialReportState.chartColumn,
    );
    const [chartType, setChartType] = useState<GraphType | undefined>(initialReportState.chartType);
    const [sortColumn, setSortColumn] = useState<ColumnSchema | undefined>(
        initialReportState.sortColumn,
    );
    const [sortDirection, setSortDirection] = useState<ColumnSortDirection | undefined>(
        initialReportState.sortDirection,
    );

    const ctx = useMemo(
        () => ({
            dateRange,
            dateRangeType,
            chartColumn,
            chartType,
            sortColumn,
            sortDirection,
            setDateRange,
            setDateRangeType,
            setChartColumn,
            setChartType,
            setSortColumn,
            setSortDirection,
        }),
        [dateRange, dateRangeType, chartColumn, chartType, sortColumn, sortDirection],
    );

    return (
        <ReportContext.Provider value={ctx}>
            <ReportTableProvider>{children}</ReportTableProvider>
        </ReportContext.Provider>
    );
};

export { ReportDetailProvider };
